import type { IMatchScheduleItem, IScheduleConstraints } from '../../../../../types/schedule';
import type { Class } from '../../../../../types/tournament';
import { flattenScheduledMatches } from '../../../../../features/schedule/scheduleOverlap';
import type { ITournamentSchedule } from '../../../../../types/schedule';
import {
  getDateString,
  getHhmmTimeString,
  getMatchDurationFromConstraints,
} from '../schedule.helpers';
import {
  formatSchedulePlayerName,
  getScheduleMatchRoundLabel,
  getScheduleTeamLabel,
  sortScheduledMatchesForPrint,
} from './schedulePrintModel';

export type ScheduleCsvRow = {
  Day: string;
  Date: string;
  StartTime: string;
  EndTime: string;
  Court: string;
  Class: string;
  Group: string;
  MatchId: string;
  Round: string;
  HomeTeam: string;
  AwayTeam: string;
  HomePlayer1: string;
  HomePlayer2: string;
  AwayPlayer1: string;
  AwayPlayer2: string;
  DurationMinutes: number;
};

const getMatchDurationMinutes = (
  match: IMatchScheduleItem,
  constraints: IScheduleConstraints | null
): number => {
  if (match.duration) return match.duration;
  if (match.scheduleDate && match.scheduleEndDate) {
    const mins =
      (new Date(match.scheduleEndDate).getTime() - new Date(match.scheduleDate).getTime()) /
      60000;
    if (Number.isFinite(mins) && mins > 0) return Math.round(mins);
  }
  return getMatchDurationFromConstraints(match, constraints) || 25;
};

export const SCHEDULE_CSV_HEADERS: { label: string; key: keyof ScheduleCsvRow }[] = [
  { label: 'Day', key: 'Day' },
  { label: 'Date', key: 'Date' },
  { label: 'StartTime', key: 'StartTime' },
  { label: 'EndTime', key: 'EndTime' },
  { label: 'Court', key: 'Court' },
  { label: 'Class', key: 'Class' },
  { label: 'Group', key: 'Group' },
  { label: 'MatchId', key: 'MatchId' },
  { label: 'Round', key: 'Round' },
  { label: 'HomeTeam', key: 'HomeTeam' },
  { label: 'AwayTeam', key: 'AwayTeam' },
  { label: 'HomePlayer1', key: 'HomePlayer1' },
  { label: 'HomePlayer2', key: 'HomePlayer2' },
  { label: 'AwayPlayer1', key: 'AwayPlayer1' },
  { label: 'AwayPlayer2', key: 'AwayPlayer2' },
  { label: 'DurationMinutes', key: 'DurationMinutes' },
];

export function buildScheduleCsvRows(
  printSchedule: ITournamentSchedule | null,
  constraints: IScheduleConstraints | null,
  timeZone: string,
  classIdToObject: Record<number, Class>,
  dayTitleForKey: (dayKey: number) => string
): ScheduleCsvRow[] {
  if (!printSchedule) return [];

  const flat = sortScheduledMatchesForPrint(flattenScheduledMatches(printSchedule), {
    classIdToObject,
  });
  const dayKeyByMatchId = new Map<number, number>();
  for (const day of printSchedule.days) {
    for (const court of day.courts) {
      for (const match of court.matches) {
        if ('id' in match && match.scheduleDate) {
          dayKeyByMatchId.set(match.id, day.dayKey);
        }
      }
    }
  }

  return flat
    .filter((m) => m.scheduleDate)
    .map((match) => {
      const dayKey = dayKeyByMatchId.get(match.id) ?? 0;
      const classObject = classIdToObject[match.classId];
      const durationMinutes = getMatchDurationMinutes(match, constraints);
      const startMs = new Date(match.scheduleDate!).getTime();
      const endIso = match.scheduleEndDate
        ? match.scheduleEndDate
        : new Date(startMs + durationMinutes * 60000).toISOString();

      return {
        Day: dayTitleForKey(dayKey),
        Date: getDateString(match.scheduleDate!, timeZone),
        StartTime: getHhmmTimeString(match.scheduleDate!, timeZone),
        EndTime: getHhmmTimeString(endIso, timeZone),
        Court: match.court?.name ?? '',
        Class: classObject?.name ?? '',
        Group: match.tournamentGroup?.name ?? '',
        MatchId: match.matchId,
        Round: getScheduleMatchRoundLabel(match, classObject),
        HomeTeam: getScheduleTeamLabel(match, 'home'),
        AwayTeam: getScheduleTeamLabel(match, 'away'),
        HomePlayer1: formatSchedulePlayerName(match.homePlayer1),
        HomePlayer2: formatSchedulePlayerName(match.homePlayer2),
        AwayPlayer1: formatSchedulePlayerName(match.awayPlayer1),
        AwayPlayer2: formatSchedulePlayerName(match.awayPlayer2),
        DurationMinutes: durationMinutes,
      };
    });
}

export function buildScheduleCsvFilename(
  tournamentId: number | undefined,
  selectedDayKey: number,
  generatedDate: string
): string {
  const dayPart = `day-${Number(selectedDayKey) + 1}`;
  const idPart = tournamentId != null ? String(tournamentId) : 'tournament';
  return `schedule-${idPart}-${dayPart}-${generatedDate}.csv`;
}
